import React, { useMemo } from "react";
import { classes } from "@bloomberg/media-ui-core";
import styles from "./styles.module.css";
import type { HeatmapTile } from "~types/types";

type HeatmapTileProps<T> = {
  tile: HeatmapTile<T>;
  enableTileFocusEvents?: boolean;
  activeTileLabel?: string;
  handleMouseMove: (
    evt: React.MouseEvent<HTMLDivElement>,
    tile: HeatmapTile<T>
  ) => void;
  handleMouseLeave: (
    evt: React.MouseEvent<HTMLDivElement>,
    tile: HeatmapTile<T>
  ) => void;
  onClick?: (tile: HeatmapTile<T>) => void;
};

function HeatmapTile<T>(props: HeatmapTileProps<T>) {
  const {
    tile,
    onClick,
    handleMouseMove,
    handleMouseLeave,
    enableTileFocusEvents,
    activeTileLabel,
  } = props;

  const {
    tileContent,
    tileSecurity,
    tileSeverity,
    tileStyle,
    tileTrend,
    tileType,
  } = useMemo(() => {
    const { data, x, y, width: w, height: h } = tile;
    const { label, trend, severity, content } = data;
    const [security, type] = label?.split(":");

    const style = {
      width: w,
      height: h,
      top: `${y}px`,
      left: `${x > 0 ? x : 0}px`,
      right: `${x < 0 ? Math.abs(x) : 0}px`,
    };

    return {
      tileContent: content,
      tileSecurity: security,
      tileSeverity: severity,
      tileStyle: style,
      tileTrend: trend,
      tileType: type,
    };
  }, [tile]);

  const tileEventHandlers = {
    onMouseMove: (evt: React.MouseEvent<HTMLDivElement>) =>
      handleMouseMove(evt, tile),
    onMouseLeave: (evt: React.MouseEvent<HTMLDivElement>) =>
      handleMouseLeave(evt, tile),
  };

  const cx = classes([
    styles.tile,
    styles[tileTrend],
    styles[tileSeverity],
    {
      [styles.focusable]: enableTileFocusEvents,
      [styles.active]: activeTileLabel === tile.data.label,
    },
  ]);

  return (
    <div
      className={cx}
      onClick={() => onClick?.(tile)}
      style={tileStyle}
      {...(enableTileFocusEvents && tileEventHandlers)}
    >
      <div className={styles.title}>
        {tileSecurity}
        <span className={styles.tickerType}>:{tileType}</span>
      </div>
      {tileContent}
    </div>
  );
}

export default React.memo(HeatmapTile) as typeof HeatmapTile;
